/**
 * The free trial a new signup starts with, as the panel sees it. The server
 * decides the lock (`cardRequired`) and says when it starts (`trialEndsAt`,
 * null when nothing will lock); the panel only keeps time between requests,
 * so a session that outlives its trial locks without a new login.
 */

/** Days before the end when the panel shows the banner (the server emails at the same mark). */
export const TRIAL_WARNING_DAYS = 7;
const DAY_MS = 24 * 60 * 60 * 1000;
/**
 * Stripe refuses a trial end less than 48 hours ahead; the server adds an
 * hour of margin (`STRIPE_MIN_TRIAL_MS`). With less left, saving the card
 * charges today, and the copy must say so.
 */
export const STRIPE_MIN_TRIAL_MS = 49 * 60 * 60 * 1000;

const TRIAL_DATE_FORMATTER = new Intl.DateTimeFormat('es-PA', { day: 'numeric', month: 'long' });

export type TrialLockFields = { cardRequired?: boolean; trialEndsAt?: string | null };

function endOf(trialEndsAt: string | null | undefined): number | undefined {
  if (!trialEndsAt) return undefined;
  const end = Date.parse(trialEndsAt);
  return Number.isNaN(end) ? undefined : end;
}

export function isTrialRunning(trialEndsAt: string | null | undefined, now: Date = new Date()): boolean {
  const end = endOf(trialEndsAt);
  return end !== undefined && now.getTime() < end;
}

/** Locked on the card step: the server said so at login, or the trial end it gave has passed. */
export function isCardLockedNow(session: TrialLockFields | null | undefined, now: Date = new Date()): boolean {
  if (!session) return false;
  if (session.cardRequired) return true;
  const end = endOf(session.trialEndsAt);
  return end !== undefined && now.getTime() >= end;
}

/** The banner's window: the last TRIAL_WARNING_DAYS of a running trial. */
export function trialWarningDue(trialEndsAt: string | null | undefined, now: Date = new Date()): boolean {
  const end = endOf(trialEndsAt);
  if (end === undefined || now.getTime() >= end) return false;
  return end - now.getTime() <= TRIAL_WARNING_DAYS * DAY_MS;
}

export function trialDaysLeft(trialEndsAt: string, now: Date = new Date()): number {
  const end = endOf(trialEndsAt) ?? now.getTime();
  return Math.max(0, Math.ceil((end - now.getTime()) / DAY_MS));
}

/** True while enough trial is left for Stripe to charge nothing until its end. */
export function holdsFirstCharge(trialEndsAt: string | null | undefined, now: Date = new Date()): boolean {
  const end = endOf(trialEndsAt);
  return end !== undefined && end - now.getTime() >= STRIPE_MIN_TRIAL_MS;
}

/** "28 de diciembre". */
export function formatTrialDate(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : TRIAL_DATE_FORMATTER.format(date);
}

/** "Prueba gratis hasta 28 de diciembre" while the trial runs; undefined otherwise. */
export function trialStatusLine(trialEndsAt: string | null | undefined, now: Date = new Date()): string | undefined {
  if (!trialEndsAt || !isTrialRunning(trialEndsAt, now)) return undefined;
  return `Prueba gratis hasta ${formatTrialDate(trialEndsAt)}`;
}

export type CoverageStatus = 'al_dia' | 'atrasado';
export type CoverageWords = { label: string; tone: 'good' | 'due' | 'off'; onTrial: boolean };

const COVERAGE_WORDS: Record<CoverageStatus, CoverageWords> = {
  al_dia: { label: 'Al día', tone: 'good', onTrial: false },
  atrasado: { label: 'Atrasado', tone: 'due', onTrial: false },
};

/**
 * The payment status word a person sees. Nothing is due during a free trial,
 * so a business inside one reads "Prueba gratis hasta ..." (neutral), never
 * "Atrasado"; a business that already paid keeps "Al día".
 */
export function coverageWords(status: CoverageStatus, trialEndsAt: string | null | undefined, now: Date = new Date()): CoverageWords {
  const trial = status === 'atrasado' ? trialStatusLine(trialEndsAt, now) : undefined;
  return trial ? { label: trial, tone: 'off', onTrial: true } : COVERAGE_WORDS[status];
}
